import { useEffect, useState, useCallback } from "react";
import { TopBar } from "../components/TopBar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { api, canManageSkus, formatRelative } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Boxes, X, Barcode, Tag, BookOpen } from "lucide-react";

const EMPTY = { sku: "", alternate_sku: "", barcode: "", description: "", notes: "", active: true };

export default function SkusPage() {
  const { user } = useAuth();
  const canEdit = canManageSkus(user);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | <id>
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/bath-skus", { params: search ? { search } : {} });
      setItems(r.data);
    } catch {
      toast.error("Couldn't load SKUs");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const openEdit = (item) => {
    setForm({
      sku: item.sku || "",
      alternate_sku: item.alternate_sku || "",
      barcode: item.barcode || "",
      description: item.description || "",
      notes: item.notes || "",
      active: item.active !== false,
    });
    setEditing(item.id);
  };

  const closeDialog = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.sku.trim() || !form.description.trim()) {
      toast.error("SKU and Description are required");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/bath-skus", form);
        toast.success("SKU added to catalogue");
      } else {
        await api.patch(`/bath-skus/${editing}`, form);
        toast.success("SKU updated");
      }
      await load();
      closeDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    const ok = window.confirm(
      `Remove "${item.sku}" from the catalogue?\n\nExisting bath records will keep their SKU text — only the catalogue entry is deleted.`
    );
    if (!ok) return;
    try {
      const r = await api.delete(`/bath-skus/${item.id}`);
      const used = r?.data?.usage ?? 0;
      toast.success(used > 0
        ? `Removed from catalogue (${used} bath record${used === 1 ? "" : "s"} still reference this SKU)`
        : "Removed from catalogue");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />

      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6">
        {/* Heading */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950 flex items-center gap-3">
              <Boxes className="h-8 w-8 text-zinc-400" /> SKU library
            </h1>
            <p className="text-sm text-zinc-600 mt-1">
              The master catalogue of every bath product you ship. Some baths carry two SKUs — add the alternate so both names stay searchable. Barcodes usually stay the same across alternates.
            </p>
          </div>
          {canEdit && (
            <Button
              onClick={openNew}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="sku-new-button"
            >
              <Plus className="h-4 w-4 mr-2" /> New SKU
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white border border-zinc-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by SKU, alternate SKU, barcode, description…"
              className="pl-9"
              data-testid="sku-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="text-left py-3 px-4 font-semibold">
                    <span className="inline-flex items-center gap-1.5"><Tag className="h-3 w-3" /> SKU</span>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">Alternate SKU</th>
                  <th className="text-left py-3 px-4 font-semibold">
                    <span className="inline-flex items-center gap-1.5"><Barcode className="h-3 w-3" /> Barcode</span>
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">Description</th>
                  <th className="text-left py-3 px-4 font-semibold">Notes</th>
                  <th className="text-left py-3 px-4 font-semibold">Updated</th>
                  <th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="text-center py-8 text-zinc-400">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="py-12 text-center text-sm text-zinc-500">
                    <BookOpen className="h-8 w-8 mx-auto mb-3 text-zinc-300" strokeWidth={1.5} />
                    No SKUs match your search.
                    {canEdit && search === "" && (
                      <div className="mt-3">
                        <Button size="sm" onClick={openNew} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="sku-empty-new">
                          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add your first SKU
                        </Button>
                      </div>
                    )}
                  </td></tr>
                )}
                {!loading && items.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-zinc-100 hover:bg-zinc-50 row-in"
                    data-testid={`sku-row-${item.sku}`}
                  >
                    <td className="py-3 px-4 font-mono text-xs font-semibold text-zinc-950 whitespace-nowrap">
                      {item.sku}
                      {item.active === false && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">
                          inactive
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-zinc-700">{item.alternate_sku || "—"}</td>
                    <td className="py-3 px-4 font-mono text-xs text-zinc-700">{item.barcode || "—"}</td>
                    <td className="py-3 px-4 text-xs text-zinc-900 max-w-[280px]">{item.description}</td>
                    <td className="py-3 px-4 text-[11px] text-zinc-500 max-w-[240px] truncate" title={item.notes || ""}>
                      {item.notes || "—"}
                    </td>
                    <td className="py-3 px-4 text-[11px] text-zinc-500 whitespace-nowrap">
                      <div>{formatRelative(item.updated_at)}</div>
                      <div className="text-[10px] text-zinc-400">{item.updated_by}</div>
                    </td>
                    <td className="py-3 px-2">
                      {canEdit && (
                        <div className="flex items-center gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(item)}
                            className="h-8 w-8 p-0"
                            data-testid={`sku-edit-${item.sku}`}
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => remove(item)}
                            className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                            data-testid={`sku-delete-${item.sku}`}
                            title="Remove"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-3 text-xs text-zinc-500 text-right">
          {items.length} SKU{items.length === 1 ? "" : "s"} in catalogue
        </div>
      </main>

      {/* Create / Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-xl" data-testid="sku-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
              <Boxes className="h-5 w-5" />
              {editing === "new" ? "Add SKU to catalogue" : "Edit SKU"}
            </DialogTitle>
            <DialogDescription>
              SKUs are matched case-insensitively. Use the Alternate SKU when a single bath ships under two product codes.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <DField label="SKU *" hint="Primary product code">
              <Input
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value })}
                className="font-mono uppercase"
                placeholder="e.g. NBA209R"
                data-testid="sku-field-sku"
              />
            </DField>
            <DField label="Alternate SKU" hint="Same bath, alternate code">
              <Input
                value={form.alternate_sku}
                onChange={(e) => setForm({ ...form, alternate_sku: e.target.value })}
                className="font-mono uppercase"
                placeholder="Optional"
                data-testid="sku-field-alt"
              />
            </DField>
            <DField label="Barcode" hint="Usually unchanged between alternates">
              <Input
                value={form.barcode}
                onChange={(e) => setForm({ ...form, barcode: e.target.value })}
                className="font-mono"
                placeholder="e.g. 5052800000099"
                data-testid="sku-field-barcode"
              />
            </DField>
            <DField label="Active" hint="Inactive SKUs are hidden from new-record autocomplete">
              <div className="flex items-center gap-3 h-9">
                <Switch
                  checked={!!form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })}
                  data-testid="sku-field-active"
                />
                <span className="text-xs text-zinc-600">{form.active ? "Visible everywhere" : "Hidden from picker"}</span>
              </div>
            </DField>
            <div className="md:col-span-2">
              <DField label="Description *">
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. 24 Chrome jets (12 Jets + 12 Airspa Jets)"
                  data-testid="sku-field-description"
                />
              </DField>
            </div>
            <div className="md:col-span-2">
              <DField label="Notes" hint="Internal notes — visible to admins and office staff only">
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  placeholder="e.g. Replaces NBA-OLD-CODE from Jan 2026; same mould different finish"
                  data-testid="sku-field-notes"
                />
              </DField>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="sku-cancel">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="sku-save"
            >
              {saving ? "Saving…" : editing === "new" ? "Add to catalogue" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DField = ({ label, hint, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="text-[11px] text-zinc-400 mt-1">{hint}</p>}
  </div>
);
